import { Inject, Injectable, OnDestroy } from '@angular/core';
import { VersionCheckConfig, VersionPayload } from '@ag-common-lib/public-api';
import { confirm } from 'devextreme/ui/dialog';
import { FirebaseApp } from 'firebase/app';
import { onSnapshot, Unsubscribe } from 'firebase/firestore';
import {
  BehaviorSubject,
  EMPTY,
  Subscription,
  debounceTime,
  filter,
  fromEvent,
  interval,
  map,
  merge,
  switchMap,
  timer,
} from 'rxjs';
import { CommonFireStoreDao } from '../dao/CommonFireStoreDao.dao';
import { FIREBASE_APP } from '../injections/firebase-app';

type VersionCheckSource = 'firestore' | 'version.json';

const DEFAULT_VERSION_CHECK_CONFIG: Required<VersionCheckConfig> = {
  enabled: true,
  firestoreCollection: 'system',
  firestoreDocumentId: 'app-version',
  versionJsonUrl: '/version.json',
  fallbackPollMs: 1000 * 60,
  afterDeclinePollMs: 1000 * 60,
  storageKey: 'app_web_version',
  snoozeStorageKey: 'app_web_version_snooze_until',
  updateNoticeText: 'New version is available. Reload now?',
  updateNoticeTitle: 'Update Available',
};

const FIRESTORE_FALLBACK_DELAY_MS = 5000;

@Injectable()
export class VersionCheckService implements OnDestroy {
  private readonly visibilitychange$ =
    typeof document !== 'undefined' ? fromEvent(document, 'visibilitychange') : EMPTY;
  private readonly fsDao: CommonFireStoreDao<any>;
  private readonly webPollMs$ = new BehaviorSubject<number>(DEFAULT_VERSION_CHECK_CONFIG.fallbackPollMs);

  private config: Required<VersionCheckConfig> = DEFAULT_VERSION_CHECK_CONFIG;
  private webSnoozeUntil = 0;
  private hasRealtimeVersionFeed = false;
  private versionJsonFallbackEnabled = false;
  private versionJsonFallbackTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private isNotificationVisible = false;
  private acceptingVersion: string | null = null;
  private versionCheckChain: Promise<void> = Promise.resolve();
  private pollingSubscription: Subscription | null = null;
  private realtimeVersionUnsubscribe: Unsubscribe | null = null;

  constructor(@Inject(FIREBASE_APP) private readonly firebaseApp: FirebaseApp) {
    this.fsDao = new CommonFireStoreDao<any>(this.firebaseApp, null, null);
  }

  public startVersionChecks(config: VersionCheckConfig = {}): void {
    this.stopVersionChecks();

    this.config = {
      ...DEFAULT_VERSION_CHECK_CONFIG,
      ...config,
    };

    if (!this.config.enabled) {
      return;
    }

    this.webPollMs$.next(this.config.fallbackPollMs);
    this.webSnoozeUntil = this.getStoredWebSnoozeUntil();
    this.versionJsonFallbackEnabled = !this.isFirestoreVersionCheckConfigured();

    this.subscribeForRealtimeVersionUpdates();
    this.scheduleVersionJsonFallback();
    this.subscribeForFallbackPolling();
  }

  public stopVersionChecks(): void {
    this.pollingSubscription?.unsubscribe();
    this.pollingSubscription = null;

    this.realtimeVersionUnsubscribe?.();
    this.realtimeVersionUnsubscribe = null;

    this.clearVersionJsonFallbackTimer();
    this.hasRealtimeVersionFeed = false;
    this.versionJsonFallbackEnabled = false;
    this.acceptingVersion = null;
  }

  protected async onVersionAccepted(_nextVersion: string): Promise<void> {
    if (typeof window !== 'undefined') {
      window.location.reload();
    }
  }

  protected onVersionRejected(_nextVersion: string, _storedVersion: string, _source: VersionCheckSource): void {}

  private subscribeForFallbackPolling() {
    const poll$ = this.webPollMs$.pipe(switchMap(ms => interval(ms)));
    const visible$ = this.visibilitychange$.pipe(
      map(() => document.visibilityState),
      filter(visibilityState => visibilityState === 'visible'),
      filter(() => Date.now() >= this.webSnoozeUntil),
    );

    this.pollingSubscription = merge(timer(0), poll$, visible$)
      .pipe(debounceTime(500))
      .subscribe(() => {
        void this.checkVersionUpdatesOnce();
      });
  }

  private async checkVersionUpdatesOnce() {
    if (this.hasRealtimeVersionFeed) {
      return;
    }

    if (this.isFirestoreVersionCheckConfigured() && !this.versionJsonFallbackEnabled) {
      return;
    }

    await this.checkVersionFromJson();
  }

  private subscribeForRealtimeVersionUpdates() {
    if (!this.isFirestoreVersionCheckConfigured()) {
      return;
    }

    try {
      const versionDocRef = this.fsDao.getDocReference(
        this.config.firestoreCollection,
        this.config.firestoreDocumentId,
      );
      if (!versionDocRef) {
        this.enableVersionJsonFallback();
        return;
      }

      this.realtimeVersionUnsubscribe = onSnapshot(
        versionDocRef,
        snapshot => {
          this.hasRealtimeVersionFeed = snapshot.exists();
          if (!snapshot.exists()) {
            this.enableVersionJsonFallback();
            return;
          }

          const nextVersion = this.extractVersion(snapshot.data() as VersionPayload);
          if (!nextVersion) {
            return;
          }

          this.handleIncomingVersion(nextVersion, 'firestore');
        },
        error => {
          this.hasRealtimeVersionFeed = false;
          this.enableVersionJsonFallback();
          console.warn('[version-check] firestore listener failed, fallback to version.json polling', error);
        },
      );
    } catch (error) {
      this.hasRealtimeVersionFeed = false;
      this.enableVersionJsonFallback();
      console.warn('[version-check] firestore listener initialization failed, fallback to version.json polling', error);
    }
  }

  private async checkVersionFromJson() {
    try {
      const response = await fetch(this.config.versionJsonUrl, { cache: 'no-store' });
      if (!response.ok) {
        return;
      }

      const payload = (await response.json()) as VersionPayload;
      const nextVersion = this.extractVersion(payload);
      if (!nextVersion) {
        return;
      }

      this.handleIncomingVersion(nextVersion, 'version.json');
    } catch (error) {
      console.warn('[version-check] failed to read version json', error);
    }
  }

  private extractVersion(payload?: VersionPayload): string | null {
    const value = payload?.version ?? payload?.build;
    if (!value) {
      return null;
    }

    return String(value);
  }

  private handleIncomingVersion(nextVersion: string, source: VersionCheckSource): void {
    void this.enqueueVersionCheck(() => this.processIncomingVersion(nextVersion, source));
  }

  private async processIncomingVersion(nextVersion: string, source: VersionCheckSource) {
    if (this.acceptingVersion === nextVersion) {
      return;
    }

    const storedVersion = this.safeLocalStorageGet(this.config.storageKey);
    if (!storedVersion) {
      this.acknowledgeVersion(nextVersion);
      console.log('[version-check] initialized', nextVersion, source);
      return;
    }

    if (storedVersion === nextVersion) {
      this.webPollMs$.next(this.config.fallbackPollMs);
      return;
    }

    if (Date.now() < this.webSnoozeUntil) {
      return;
    }

    const confirmed = await this.noticeAboutUpdates(nextVersion);
    if (confirmed === null) {
      return;
    }

    if (!confirmed) {
      this.acknowledgeVersion(nextVersion);
      this.applyVersionSnooze();
      this.onVersionRejected(nextVersion, storedVersion, source);
      return;
    }

    await this.onVersionAccepted(nextVersion);
  }

  private noticeAboutUpdates = async (nextVersion: string): Promise<boolean | null> => {
    if (this.isNotificationVisible || this.acceptingVersion === nextVersion) {
      return null;
    }

    this.isNotificationVisible = true;
    const confirmed = await confirm(this.config.updateNoticeText, this.config.updateNoticeTitle);

    if (confirmed) {
      this.acknowledgeVersion(nextVersion);
    }

    this.isNotificationVisible = false;
    return confirmed;
  };

  private acknowledgeVersion(nextVersion: string): void {
    this.acceptingVersion = nextVersion;
    this.safeLocalStorageSet(this.config.storageKey, nextVersion);
    this.clearVersionSnooze();
    this.webPollMs$.next(this.config.fallbackPollMs);
  }

  private applyVersionSnooze() {
    this.webSnoozeUntil = Date.now() + this.config.afterDeclinePollMs;
    this.safeLocalStorageSet(this.config.snoozeStorageKey, String(this.webSnoozeUntil));
    this.webPollMs$.next(this.config.afterDeclinePollMs);
  }

  private clearVersionSnooze() {
    this.webSnoozeUntil = 0;
    this.safeLocalStorageRemove(this.config.snoozeStorageKey);
    this.webPollMs$.next(this.config.fallbackPollMs);
  }

  private scheduleVersionJsonFallback(): void {
    this.clearVersionJsonFallbackTimer();

    if (!this.isFirestoreVersionCheckConfigured()) {
      return;
    }

    this.versionJsonFallbackTimeoutId = setTimeout(() => {
      this.versionJsonFallbackTimeoutId = null;
      if (!this.hasRealtimeVersionFeed) {
        this.enableVersionJsonFallback();
      }
    }, FIRESTORE_FALLBACK_DELAY_MS);
  }

  private enableVersionJsonFallback(): void {
    this.versionJsonFallbackEnabled = true;
  }

  private clearVersionJsonFallbackTimer(): void {
    if (this.versionJsonFallbackTimeoutId === null) {
      return;
    }

    clearTimeout(this.versionJsonFallbackTimeoutId);
    this.versionJsonFallbackTimeoutId = null;
  }

  private isFirestoreVersionCheckConfigured(): boolean {
    return Boolean(this.config.firestoreCollection && this.config.firestoreDocumentId);
  }

  private enqueueVersionCheck(task: () => Promise<void>): void {
    this.versionCheckChain = this.versionCheckChain
      .then(task)
      .catch(error => console.warn('[version-check] version check failed', error));
  }

  private getStoredWebSnoozeUntil(): number {
    const rawValue = this.safeLocalStorageGet(this.config.snoozeStorageKey);
    if (!rawValue) {
      return 0;
    }

    const parsedValue = Number(rawValue);
    if (!Number.isFinite(parsedValue) || parsedValue <= Date.now()) {
      this.safeLocalStorageRemove(this.config.snoozeStorageKey);
      return 0;
    }

    return parsedValue;
  }

  private safeLocalStorageGet(key: string): string | null {
    if (typeof localStorage === 'undefined') {
      return null;
    }
    return localStorage.getItem(key);
  }

  private safeLocalStorageSet(key: string, value: string): void {
    if (typeof localStorage === 'undefined') {
      return;
    }
    localStorage.setItem(key, value);
  }

  private safeLocalStorageRemove(key: string): void {
    if (typeof localStorage === 'undefined') {
      return;
    }
    localStorage.removeItem(key);
  }

  ngOnDestroy(): void {
    this.stopVersionChecks();
  }
}
/*
* Should be one authoritative source on load (prefer firestore than version.json)
* */
